import { useCallback, useState } from 'react';

import { useNeighborData } from './NeighborDataContext';

// useAnalyserGate wraps an analyser fetcher with the shared pieces
// every panel needs around neighbour-data freshness:
//
//   * catches 409 (neighbor_data_incomplete) and surfaces the
//     `missing_serials` list so the panel can render the incomplete
//     card instead of just a toast,
//   * pushes the freshness block from a successful response into the
//     shared NeighborDataContext so every panel's chip refreshes,
//   * exposes a `scanMissing` helper pre-bound to the current
//     missing list so the incomplete card's scan button is trivial.
export const useAnalyserGate = () => {
  const { mergeFreshness, scanDevices, scanningSerials } = useNeighborData();
  const [missingSerials, setMissingSerials] = useState([]);

  const runGuarded = useCallback(async (fetcher) => {
    setMissingSerials([]);
    try {
      const data = await fetcher();
      if (data?.data_freshness) {
        mergeFreshness(data.data_freshness);
      }
      return { ok: true, data };
    } catch (err) {
      if (err?.statusCode === 409 && Array.isArray(err?.data?.missing_serials)) {
        setMissingSerials(err.data.missing_serials);
        if (err.data.data_freshness) {
          mergeFreshness(err.data.data_freshness);
        }
        return { ok: false, gated: true, error: err };
      }
      return { ok: false, gated: false, error: err };
    }
  }, [mergeFreshness]);

  const scanMissing = useCallback(async () => {
    if (missingSerials.length === 0) return null;
    return scanDevices(missingSerials);
  }, [scanDevices, missingSerials]);

  return {
    missingSerials,
    clearMissing: () => setMissingSerials([]),
    runGuarded,
    scanMissing,
    scanningSerials,
  };
};

export default useAnalyserGate;
